import { Link } from 'react-router-dom';
import { featuredMovies, latestMovies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link to={`/detail/${movie.id}`} className="group block cursor-pointer">
      <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3">
        <img
          src={movie.image}
          alt={`${movie.title} - 51影院 | 久久影院 | 悠久影院`}
          title={`${movie.title} - 51影院免费在线观看`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute top-2 left-2">
          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500 text-background-50">
            {movie.rating}
          </span>
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 rounded-md text-xs bg-background-950/70 text-background-100">
            {movie.updateStatus}
          </span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <p className="text-background-50 text-xs line-clamp-3">{movie.description}</p>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
        {movie.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-1">
        {movie.subcategory} · {movie.year}
      </p>
    </Link>
  );
}

function FeaturedMovieCard({ movie, index }: { movie: Movie; index: number }) {
  return (
    <Link to={`/detail/${movie.id}`} className="group block relative overflow-hidden rounded-lg h-[360px] md:h-[420px] cursor-pointer">
      <img
        src={movie.image}
        alt={`${movie.title} - 51影院推荐`}
        title={`${movie.title} - 免费在线观看`}
        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background-950/85 via-background-950/20 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-accent-500 text-background-50">{movie.rating}</span>
          <span className="text-xs text-background-200">{movie.subcategory}</span>
          <span className="text-xs text-background-300">{movie.year}</span>
        </div>
        <h3 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-2">{movie.title}</h3>
        <p className="text-sm text-background-300 line-clamp-2 mb-3">{movie.description}</p>
        <div className="flex items-center gap-3 text-xs text-background-400">
          <span><i className="ri-eye-line mr-1"></i>{(movie.viewCount / 10000).toFixed(0)}万次</span>
          <span><i className="ri-user-line mr-1"></i>{movie.director}</span>
        </div>
      </div>
      <div className="absolute top-3 left-3">
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-background-950/60 text-background-100 backdrop-blur-sm">
          热播推荐 #{index + 1}
        </span>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '51影院',
    url: 'https://www.51cinema.com',
    description: '51影院提供久久影院、悠久影院等全类型影视资源，电视剧大全、电影大全、综艺节目一应俱全，无需注册免费在线观看。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.51cinema.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <section className="relative w-full h-[420px] md:h-[550px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20movie%20theater%20interior%20with%20warm%20golden%20spotlight%20illuminating%20red%20velvet%20seats%2C%20dramatic%20atmosphere%2C%20luxurious%20and%20elegant%20design%2C%20soft%20ambient%20lighting%2C%20rich%20textures%2C%20empty%20theater%20with%20grand%20curtain%2C%20professional%20cinema%20photography&width=1920&height=800&seq=hero-bg-51cinema&orientation=landscape"
          alt="51影院 - 久久影院 悠久影院 免费在线影视"
          title="51影院 - 免费高清影视在线观看"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/50 via-background-950/30 to-background-950/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 tracking-tight">
            海量影视，<span className="text-primary-400">免费畅享</span>
          </h1>
          <p className="text-sm md:text-lg text-background-200 max-w-2xl mb-8 leading-relaxed">
            51影院聚合<strong>久久影院</strong>、<strong>悠久影院</strong>、<strong>九九影院</strong>、<strong>55影院</strong>等优质资源，<strong>电视剧大全</strong>、<strong>电影大全</strong>、<strong>综艺节目</strong>一网打尽，无需注册，即点即看。
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link to="/category/movie" className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-film-line mr-2"></i>浏览电影大全
            </Link>
            <Link to="/category/tv" className="px-8 py-3 rounded-full bg-background-50/20 text-background-50 font-semibold text-sm hover:bg-background-50/30 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-tv-line mr-2"></i>浏览电视剧大全
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {categories.slice(1).map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-secondary-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className={`${cat.icon} text-base`}></i>
              {cat.name}
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            <i className="ri-fire-line text-primary-500 mr-2"></i>热门推荐
          </h2>
          <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            查看全部 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {featuredMovies.map((movie, index) => (
            <FeaturedMovieCard key={movie.id} movie={movie} index={index} />
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              <i className="ri-time-line text-accent-500 mr-2"></i>最新更新
            </h2>
            <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              更多更新 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
          为什么选择<span className="text-primary-500">51影院</span>
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            { icon: 'ri-movie-2-line', title: '资源全面', desc: '聚合久久影院、悠久影院等多家平台，电视剧大全、电影大全、综艺节目全覆盖。' },
            { icon: 'ri-4k-line', title: '高清画质', desc: '1080P及4K超高清画质，影院级观影体验。' },
            { icon: 'ri-user-unfollow-line', title: '无需注册', desc: '即点即看无需繁琐注册，保护您的隐私安全。' },
            { icon: 'ri-refresh-line', title: '极速更新', desc: '7x24小时不间断更新，追剧不等待。' },
          ].map((item, i) => (
            <div key={i} className="text-center p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className={`${item.icon} text-xl`}></i>
              </div>
              <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">{item.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative w-full py-14 md:py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20home%20theater%20setup%20with%20a%20large%20screen%20displaying%20colorful%20abstract%20visuals%2C%20cozy%20atmosphere%20with%20soft%20ambient%20lighting%2C%20modern%20living%20room%2C%20comfortable%20couch%2C%20entertainment%20aesthetic%2C%20warm%20golden%20tones&width=1920&height=600&seq=cta-bg-51cinema&orientation=landscape"
          alt="51影院 - 免费在线观看"
          title="51影院 - 随时随地免费看片"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-background-950/70"></div>
        <div className="relative z-10 text-center px-4 w-full">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
            随时随地，免费看大片
          </h2>
          <p className="text-sm md:text-base text-background-200 max-w-xl mx-auto mb-6">
            51影院支持电脑、平板、手机全设备访问，无需下载APP，浏览器打开即可畅享海量影视资源。
          </p>
          <Link to="/category/movie" className="inline-flex px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            立即开始观影 <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>
    </>
  );
}